import React from "react";
import {
  Row,
  Col,
  Button,
  Alert,
  Modal as BootstrapModal,
} from "react-bootstrap";
import BigCalendar from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "./calendar.css";
import moment from "moment";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import HTML5Backend from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { DragDropContext } from "react-dnd";
import Engineers from "./Engineers";
import { CustomToolbar } from "./CustomToolbar";
import Shift from "../forms/Shift";
import CustomEvent from "./CustomEvent";
import timezones from "../../../../components/data/timezones.json";
import Preview from "react-dnd-preview";

// The big calendar needs a localization strategy
// The big calendar is already a drop target when using the drag and drop addon
// These are needed for drag and drop
// I think that HTML5Backend does not work on mobiles
// and a different backend should be used there.
// Some more research on react-dnd is needed.
// These are hardcoded data
// import schedule from './schedule'
// import engineers from './engineeers'
// This is our drag source enabled list of engineers.

// Initialize localization strategy
BigCalendar.setLocalizer(BigCalendar.momentLocalizer(moment));

// That backend parameter there...
// I didn't find it documented - had to look in the code
// If it's not passed like this, then there will be another
// DragAndDropContext created just at the calendar level.
// We don't need that, since we need to be able to drop from
// outside the calendar.
const DragAndDropCalendar = withDragAndDrop(BigCalendar, { backend: false });

const isTouchDevice = !!("ontouchstart" in window || navigator.maxTouchPoints);
// This will contain the calendar
// And the list of engineers
// And it will manage the dragging from the list to the calendar
class ScheduleEditor extends React.Component {
  constructor(props) {
    super(props);
    this.onEventDrop = this.onEventDrop.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
  }

  /* istanbul ignore next */
  previewGenerator(itemType, item, style) {
    let engineerName;
    if (item.event.name !== undefined) {
      engineerName = item.event.name;
    } else if (item.event.engineer !== undefined) {
      engineerName =
        item.event.engineer.firstName + " " + item.event.engineer.lastName;
    }
    const styleDiv = {
      minWidth: "120px",
      ...style,
    };
    return (
      <div className="rbc-event" style={styleDiv}>
        {engineerName}
      </div>
    );
  }

  onEventDrop({ event, start, end }) {
    const shiftsDefinitions = this.props.shiftsDefinitions;
    const engineers = this.props.engineers;
    const region = this.props.region;
    // There are 2 possibilities here
    // Either a shift definition is moved from another day
    // Or a shift definition is created by moving an engineer into a day

    // When a shift definition is created, the shift definition received here
    // will only have the 'id' property of the DraggableEngineer component.
    // That property is negative, as opposed to all shift definitions ids, which are positive
    if (event.id < 0) {
      const engineer = engineers.find(engineer => engineer.id === -event.id);
      // this is a new shift definition
      // we're adding it to the schedule

      // This is actually a good place to trigger an API call and
      // disable the UI until the backend confirmation comes.
      // This would also be a good place to remove the engineer
      // from the list if the checkbox is in the right state.
      const toDate = moment(start)
        .add(region.defaultShiftDays, "days")
        .format("YYYY-MM-DD");
      const newShiftDefinition = {
        engineer,
        start: moment(start).format("YYYY-MM-DD"),
        end: toDate,
        priority: event.priority,
      };
      this.props.onNewShiftDefinition(newShiftDefinition);
    } else {
      // this is an existing shift definition which is being dragged
      const originalShiftDefinition = shiftsDefinitions.find(
        definition => definition.id === event.id,
      );

      // We're updating the shift definition with the new start and end date
      const updatedShiftDefinition = { ...originalShiftDefinition, start, end };
      this.props.onMoveShiftDefinition(originalShiftDefinition)(
        updatedShiftDefinition,
      );
    }
  }

  handleDelete() {
    this.props.onDeleteShiftDefinition(this.props.selected);
  }

  render() {
    let selected = undefined;
    if (this.props.selected !== null) {
      selected = this.props.shiftsDefinitions.find(
        definition => definition.id === this.props.selected.id,
      );
    }

    const timeComponents = this.props.region.defaultShiftChangeTime.split(":");
    const defaultShiftChangeTime = moment()
      .hour(timeComponents[0])
      .minute(timeComponents[1])
      .format("hh:mm A");
    const timeZoneKey = this.props.region.timeZone;
    const timeZone = Object.keys(timezones).find(
      key => timezones[key] === timeZoneKey,
    );
    const defaultShiftDays = this.props.region.defaultShiftDays;

    let dndMobilePreview = undefined;
    /* istanbul ignore next */
    if (isTouchDevice) {
      dndMobilePreview = <Preview generator={this.previewGenerator} />;
    }
    return (
      <div id="region-schedule">
        <Row className={"region-schedule-container"}>
          <Col md={12}>
            <Alert bsStyle="info">
              <strong>Default Shift Change Time</strong>{" "}
              {defaultShiftChangeTime}
              <br />
              <strong>Default Shift Duration</strong> {defaultShiftDays} days
              <br />
              <strong>Timezone</strong> {timeZone}
            </Alert>
          </Col>
        </Row>
        <Row className={"region-schedule-container"}>
          <Col md={2} className={"engineers-container"}>
            <h3>Engineers</h3>
            <Engineers engineers={this.props.engineers} />
            {dndMobilePreview}
            <h3>Shift</h3>
            <Shift
              key={`shift-${selected === undefined ? "alert" : selected.id}`}
              shift={selected}
              onSave={this.props.onUpdateShiftDefinition(selected)}
              onDelete={this.handleDelete}
            />
          </Col>
          <Col md={10}>
            {/*This is where one would add a checkbox to keep track*/}
            {/*whether the engineer should be removed from the list */}
            {/*of available engineers or not*/}
            <DragAndDropCalendar
              // Only show the month view
              views={["month"]}
              defaultView="month"
              // Allow the "x more" popup
              popup
              // Setting the title of each event from the schedule
              // as the name of the engineer assigned to that event.
              events={this.props.shiftsDefinitions}
              // Handler for when an engineer is dropped onto a slot
              onEventDrop={this.onEventDrop}
              selectable
              selected={selected}
              onSelectEvent={this.props.onSelect}
              // And some workarounds because *everything* is broken (TM)
              // If a specific height is not provided
              // Then the month view will not be visible
              // https://github.com/intljusticemission/react-big-calendar/issues/175
              style={{ height: "700px" }}
              // If you don't set the default date, the next/back buttons won't work
              // https://github.com/intljusticemission/react-big-calendar/issues/718
              defaultDate={new Date()}
              components={{
                toolbar: CustomToolbar,
                event: CustomEvent,
              }}
              titleAccessor={({ start, engineer, priority }) => {
                if (engineer.firstName === "FREE") {
                  return `${moment(start).format("HH:mm")} ${
                    engineer.firstName
                  } ${engineer.lastName}`;
                } else {
                  return `#${priority} ${moment(start).format("HH:mm")} ${
                    engineer.firstName
                  } ${engineer.lastName}`;
                }
              }}
              tooltipAccessor={shift => {
                const format = "YYYY-MM-DD hh:mm A";
                const start = moment(shift.start).format(format);
                const end = moment(shift.end).format(format);
                return `Priority: #${shift.priority}${"\n"}${
                  shift.engineer.firstName
                } ${shift.engineer.lastName}${"\n"}${start}${"\n"}${end}`;
              }}
            />
            <div className={"text-center"}>
              <br />
              <a
                className={"btn btn-primary center-back-button"}
                id="back-to-regions"
                onClick={this.props.onRegionList}
              >
                Back to regions
              </a>
              <br />
            </div>
          </Col>
        </Row>

        <BootstrapModal
          id="dismiss-error-modal"
          show={this.props.uiError !== ""}
          onHide={this.props.onDismissError}
        >
          <BootstrapModal.Header closeButton>
            <BootstrapModal.Title>
              Schedule couldn't be added
            </BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <div>{this.props.uiError}</div>
          </BootstrapModal.Body>
          <BootstrapModal.Footer>
            <Button
              id="close-dismiss-modal"
              className="btn btn-primary "
              onClick={this.props.onDismissError}
            >
              Close
            </Button>
          </BootstrapModal.Footer>
        </BootstrapModal>
      </div>
    );
  }
}

// TODO: Resolve this in such a way that react-dnd-test-backend is not included in the build.
// Obviously in tests this will always result in loading the test backend, so we can't cover the other branch.
/* istanbul ignore next */
const backend =
  process.env.NODE_ENV === "test"
    ? require("react-dnd-test-backend")
    : isTouchDevice
    ? TouchBackend
    : HTML5Backend;

export default DragDropContext(backend)(ScheduleEditor);



// WEBPACK FOOTER //
// ./src/sc/ui/sites/oncall/components/calendar/EditableCalendar.js